import React, { useEffect, useRef, useState } from 'react';
import { cooperplaceApi } from 'services/api';
import { TollsPlaza } from 'v3/components/TollsPlaza';
import { Text } from 'v3/components';
import * as Styled from './styles';

export const EMPTY_TOLLS = {
	tollsPlaza: [],
	totalTollValue: 0,
	totalTollTagValue: 0,
	totalKm: 0,
	expectedTime: '0',
	identifierHistory: '0',
	codPolilyne: 0,
	totalAxes: 0,
	requestError: false,
	calculationMessage: '',
};

export const MISSING_VEHICLE_TOLLS_MESSAGE =
	'Não foi possível calcular o pedágio porque não foram inseridas informações no card de veículos.';

export function normalizeTollsPlazaResponse(payload) {
	if (!payload) return { ...EMPTY_TOLLS };

	const source = Array.isArray(payload) ? payload[0] : payload;
	if (!source) return { ...EMPTY_TOLLS };

	const totals = source?.totalsTollsPlaza || source?.totals_tolls_plaza || {};
	const rawTolls =
		source?.tollsPlaza ??
		source?.tolls_plaza ??
		source?.tolls ??
		[];

	let tollsPlaza = rawTolls;

	if (typeof rawTolls === 'string') {
		try {
			tollsPlaza = JSON.parse(rawTolls);
		} catch {
			tollsPlaza = [];
		}
	}

	if (!Array.isArray(tollsPlaza)) {
		tollsPlaza = [];
	}

	return {
		...EMPTY_TOLLS,
		...source,
		totalTollValue: Number(
			totals?.totalTollValue ??
			totals?.total_toll_value ??
			source?.totalTollValue ??
			source?.total_toll_value ??
			0,
		),
		totalTollTagValue: Number(
			totals?.totalTollTagValue ??
			totals?.total_toll_tag_value ??
			source?.totalTollTagValue ??
			source?.total_toll_tag_value ??
			0,
		),
		totalKm: Number(
			totals?.totalKm ??
			totals?.total_km ??
			source?.totalKm ??
			source?.total_km ??
			0,
		),
		expectedTime:
			totals?.expectedTime ??
			totals?.expected_time ??
			source?.expectedTime ??
			source?.expected_time ??
			'0',
		identifierHistory:
			totals?.identifierHistory ??
			totals?.identifier_history ??
			source?.identifierHistory ??
			source?.identifier_history ??
			'0',
		codPolilyne: Number(
			totals?.codPolilyne ??
			totals?.cod_polilyne ??
			source?.codPolilyne ??
			source?.cod_polilyne ??
			0,
		),
		totalAxes: Number(
			totals?.totalAxes ??
			totals?.total_axes ??
			source?.totalAxes ??
			source?.total_axes ??
			0,
		),
		tollsPlaza: tollsPlaza.map((item) => ({
			...item,
			lat: Number(item?.lat ?? item?.latitude ?? 0),
			long: Number(item?.long ?? item?.lng ?? item?.longitude ?? 0),
			tollValue: Number(item?.tollValue ?? item?.toll_value ?? 0),
			name: item?.name ?? item?.toll_name ?? item?.plaza_name ?? '',
			address: item?.address ?? item?.formatted_address ?? item?.location ?? '',
		})),
		requestError: Boolean(source?.requestError ?? source?.request_error),
	};
}

function uniqueTruthy(items = []) {
	return Array.from(new Set((items || []).filter(Boolean)));
}

function resolveVehicleTypeId(vehicle) {
	return (
		vehicle?.vehicle_type_id ||
		vehicle?.vehicleTypeId ||
		vehicle?.vehicleType?.id ||
		vehicle?.vehicle_type?.id ||
		null
	);
}

function resolveImplementTypeId(implement) {
	return (
		implement?.vehicle_implement_type_id ||
		implement?.vehicleImplementTypeId ||
		implement?.implementType?.id ||
		implement?.vehicle_implement_type?.id ||
		null
	);
}

export function useTollsPlazaCalculation({
	enabled = false,
	polyline = null,
	load = null,
	loadCreated = null,
	loadCreatedGro = null,
	travelData = null,
	setTollsPlazaInfos,
	setTollsPlazaLoadingGro,
	onError,
	debounceMs = 700,
}) {
	const activeRequestsRef = useRef(0);
	const loadingCycleRef = useRef(0);
	const onErrorRef = useRef(onError);
	const setTollsPlazaLoadingGroRef = useRef(setTollsPlazaLoadingGro);
	const [isLoading, setIsLoading] = useState(false);

	const loadTravelVehicleTypeIds = uniqueTruthy([
		load?.travel?.vehicle?.[0]?.vehicles?.vehicleTypeId,
		load?.travel?.vehicle?.[0]?.vehicleTypeId,
		load?.travel?.vehicle?.[0]?.vehicles?.vehicle_type_id,
		...(load?.vehicleTypes || []).map(vehicle => vehicle?.id),
	]);
	const loadTravelImplementsTypesIds = uniqueTruthy([
		...(load?.travel?.vehicle?.[0]?.wagons || []).map(
			item => item?.vehicleImplementTypeId || item?.vehicle_implement_type_id,
		),
		...(load?.vehicleImplementTypes || []).map(implement => implement?.id),
	]);

	const travelMainVehicleTypeId = resolveVehicleTypeId(travelData?.vehicle?.main);
	const travelVehicleTypesIds = uniqueTruthy([
		travelMainVehicleTypeId,
		...(travelData?.vehicleTypes || []).map(vehicleType => vehicleType?.id),
	]);
	const travelImplementsTypesIds = uniqueTruthy([
		resolveImplementTypeId(travelData?.vehicle?.implement),
		resolveImplementTypeId(travelData?.vehicle?.implement2),
		resolveImplementTypeId(travelData?.vehicle?.implement3),
		...(travelData?.vehicleImplementTypes || []).map(implement => implement?.id),
	]);

	const loadVehicleTypesIds = uniqueTruthy(
		(load?.vehicleTypes || []).map(vehicleType => vehicleType?.id),
	);
	const loadImplementsTypesIds = uniqueTruthy(
		(load?.vehicleImplementTypes || []).map(implement => implement?.id),
	);

	const loadCreatedVehicleTypesIds = uniqueTruthy(
		(loadCreated?.selectedVehicles || []).map(vehicle => vehicle?.id),
	);
	const loadCreatedImplementsTypesIds = uniqueTruthy(
		(loadCreated?.selectedImplements || []).map(implement => implement?.id),
	);

	const loadCreatedGroVehicleTypesIds = uniqueTruthy(
		(loadCreatedGro?.vehicleTypes || []).map(vehicleType => vehicleType?.id),
	);
	const loadCreatedGroImplementsTypesIds = uniqueTruthy(
		(loadCreatedGro?.vehicleImplements || []).map(implement => implement?.id),
	);

	const loadTravelVehicleTypesKey = JSON.stringify(loadTravelVehicleTypeIds);
	const loadTravelImplementsKey = JSON.stringify(loadTravelImplementsTypesIds);
	const travelVehicleTypesKey = JSON.stringify(travelVehicleTypesIds);
	const travelImplementsKey = JSON.stringify(travelImplementsTypesIds);
	const loadVehicleTypesKey = JSON.stringify(loadVehicleTypesIds);
	const loadImplementsKey = JSON.stringify(loadImplementsTypesIds);
	const loadCreatedVehiclesKey = JSON.stringify(loadCreatedVehicleTypesIds);
	const loadCreatedImplementsKey = JSON.stringify(loadCreatedImplementsTypesIds);
	const loadCreatedGroVehiclesKey = JSON.stringify(loadCreatedGroVehicleTypesIds);
	const loadCreatedGroImplementsKey = JSON.stringify(loadCreatedGroImplementsTypesIds);

	useEffect(() => {
		onErrorRef.current = onError;
	}, [onError]);

	useEffect(() => {
		setTollsPlazaLoadingGroRef.current = setTollsPlazaLoadingGro;
	}, [setTollsPlazaLoadingGro]);

	const notifyTollsPlazaLoadingGro = (value) => {
		if (typeof setTollsPlazaLoadingGroRef.current === 'function') {
			setTollsPlazaLoadingGroRef.current(value);
		}
	};

	useEffect(() => {
		const currentCycle = ++loadingCycleRef.current;

		if (typeof setTollsPlazaInfos !== 'function') return undefined;

		if (!enabled) {
			setIsLoading(false);
			notifyTollsPlazaLoadingGro(false);
			return undefined;
		}

		if (!polyline) {
			setIsLoading(false);
			notifyTollsPlazaLoadingGro(false);
			setTollsPlazaInfos({ ...EMPTY_TOLLS });
			return undefined;
		}

		const controller = new AbortController();
		const signal = controller.signal;
		const isStaleCycle = () =>
			signal.aborted || currentCycle !== loadingCycleRef.current;
		let started = false;

		setIsLoading(true);
		notifyTollsPlazaLoadingGro(true);

		const fetchTolls = async () => {
			started = true;
			activeRequestsRef.current += 1;

			try {
				let payload = null;
				let payloadSource = null;

				if (load?.travel && loadTravelVehicleTypeIds.length > 0) {
					payload = {
						polyline,
						vehiclesTypesIds: loadTravelVehicleTypeIds,
						implementsTypesIds: loadTravelImplementsTypesIds,
					};
					payloadSource = 'load.travel';
				} else if (travelData && travelVehicleTypesIds.length > 0) {
					payload = {
						polyline,
						vehiclesTypesIds: travelVehicleTypesIds,
						implementsTypesIds: travelImplementsTypesIds,
					};
					payloadSource = 'travelData';
				} else if (load?.id && !load?.travel && loadVehicleTypesIds.length > 0) {
					payload = {
						polyline,
						vehiclesTypesIds: loadVehicleTypesIds,
						implementsTypesIds: loadImplementsTypesIds,
					};
					payloadSource = 'load';
				} else if (loadCreated && loadCreatedVehicleTypesIds.length > 0) {
					payload = {
						polyline,
						vehiclesTypesIds: loadCreatedVehicleTypesIds,
						implementsTypesIds: loadCreatedImplementsTypesIds,
					};
					payloadSource = 'loadCreated';
				} else if (loadCreatedGro && loadCreatedGroVehicleTypesIds.length > 0) {
					payload = {
						polyline,
						vehiclesTypesIds: loadCreatedGroVehicleTypesIds,
						implementsTypesIds: loadCreatedGroImplementsTypesIds,
					};
					payloadSource = 'loadCreatedGro';
				}

				const hasValidPayload =
					payload?.polyline &&
					Array.isArray(payload?.vehiclesTypesIds) &&
					payload.vehiclesTypesIds.length > 0;

				if (!hasValidPayload) {
					if (isStaleCycle()) return;
					setTollsPlazaInfos({
						...EMPTY_TOLLS,
						requestError: true,
						calculationMessage: MISSING_VEHICLE_TOLLS_MESSAGE,
					});
					return;
				}

				const response = await cooperplaceApi.post(
					'tollsPlaza/integration-extratta',
					payload,
					{ signal },
				);

				if (isStaleCycle()) return;
				setTollsPlazaInfos(normalizeTollsPlazaResponse(response?.data));
			} catch (error) {
				if (isStaleCycle()) return;
				setTollsPlazaInfos({
					...EMPTY_TOLLS,
					requestError: true,
				});

				if (typeof onErrorRef.current === 'function') {
					onErrorRef.current(error);
				}
			} finally {
				activeRequestsRef.current -= 1;

				if (currentCycle !== loadingCycleRef.current) {
					return;
				}

				if (activeRequestsRef.current === 0) {
					setIsLoading(false);
					notifyTollsPlazaLoadingGro(false);
				}
			}
		};

		const timeoutId = setTimeout(fetchTolls, debounceMs);

		return () => {
			clearTimeout(timeoutId);
			controller.abort();

			if (
				currentCycle === loadingCycleRef.current &&
				!started &&
				activeRequestsRef.current === 0
			) {
				setIsLoading(false);
				notifyTollsPlazaLoadingGro(false);
			}
		};
	}, [
		enabled,
		polyline,
		load?.id,
		load?.travel?.id,
		loadTravelVehicleTypesKey,
		loadTravelImplementsKey,
		travelData?.loadId,
		travelVehicleTypesKey,
		travelImplementsKey,
		loadVehicleTypesKey,
		loadImplementsKey,
		loadCreatedVehiclesKey,
		loadCreatedImplementsKey,
		loadCreatedGroVehiclesKey,
		loadCreatedGroImplementsKey,
		setTollsPlazaInfos,
		debounceMs,
	]);

	return {
		isLoading,
	};
}

export function TollsPlazaCalculationCard({
	tollsPlazaInfos,
	setTollsPlazaInfos,
	enableEdit = false,
	polyline = null,
	load = null,
	loadCreated = null,
	loadCreatedGro = null,
	travelData = null,
	takerValue = null,
	setTollsPlazaLoadingGro,
	onLoadingChange,
	snackbar,
}) {
	const tollsCardTakerValue = Number(
		takerValue ??
		load?.takerValue ??
		load?.taker_value ??
		travelData?.takerValue ??
		travelData?.taker_value ??
		travelData?.load?.takerValue ??
		travelData?.load?.taker_value ??
		loadCreated?.takerValue ??
		loadCreated?.taker_value ??
		loadCreatedGro?.takerValue ??
		loadCreatedGro?.taker_value ??
		0,
	);

	const { isLoading } = useTollsPlazaCalculation({
		enabled: enableEdit,
		polyline,
		load,
		loadCreated,
		loadCreatedGro,
		travelData,
		setTollsPlazaInfos,
		setTollsPlazaLoadingGro,
		onError: (error) => {
			if (!snackbar?.show) return;
			snackbar.show(
				<Text>
					{error?.response?.data?.message || 'Erro ao listar praças de pedágio'}
				</Text>,
				{ type: 'error' },
			);
		},
	});

	useEffect(() => {
		if (typeof onLoadingChange === 'function') {
			onLoadingChange(isLoading);
		}
	}, [isLoading, onLoadingChange]);

	return (
		<Styled.Wrapper>
			<TollsPlaza
				tollsPlazaInfos={tollsPlazaInfos || EMPTY_TOLLS}
				tollsPlazaLoading={isLoading}
				takerValue={tollsCardTakerValue}
			/>
		</Styled.Wrapper>
	);
}

export default useTollsPlazaCalculation;
